import { NOM } from './nom'
import type { Ton } from './Marque'

interface Props {
  /**
   * Hauteur du bloc en pixels. Une seule mesure commande l'ensemble, donc le
   * verrouillage reste juste à toute échelle.
   */
  taille?: number
  /**
   * Le verrouillage EMPILÉ — marque au-dessus, mot en dessous.
   *
   * Il sert là où la largeur manque : une colonne étroite, une carte, un
   * en-tête de courriel sur téléphone. Ailleurs, l'horizontal se lit mieux.
   */
  empile?: boolean
  ton?: Ton
  className?: string
}

/**
 * Le logotype complet — 06 §8, [DECISIONS §295].
 *
 * **Ce composant composait le mot en TEXTE, et ne le fait plus.** L'ancienne
 * note défendait ce choix avec de bons arguments — un candidat paie ses données
 * à la mégaoctet, et du vrai texte se lit, se traduit, se grossit et s'annonce.
 * Ces arguments valaient tant qu'aucun logotype n'existait : le mot était alors
 * composé avec la police du système, faute de mieux.
 *
 * Le designer en a livré un, avec sa typographie. Recomposer le nom dans une
 * autre fonte, c'est afficher un logotype qui n'est pas le sien — et le premier
 * écran d'un produit est le dernier endroit où l'à-peu-près passe inaperçu.
 *
 * **Ce que le changement coûte, et comment on le paie.** Une image de 128 px de
 * haut pèse une dizaine de kilo-octets — moins qu'une seule photo de question.
 * Le nom accessible revient par `alt`, qui porte le mot exact ; les lecteurs
 * d'écran l'annoncent comme avant.
 */
export function Logotype({
  taille = 31,
  empile = false,
  ton = 'couleur',
  className = '',
}: Props) {
  const nom = empile ? 'logotype-empile' : 'logotype'

  return (
    <img
      src={`/marque/${nom}-${ton}-${String(sourceLaPlusProche(taille))}.png`}
      alt={NOM}
      // Seule la hauteur est imposée : la largeur suit le rapport du fichier,
      // qui n'est pas le même pour l'horizontal et pour l'empilé.
      height={taille}
      className={className}
      style={{ height: `${String(taille)}px`, width: 'auto', flexShrink: 0 }}
    />
  )
}

/** Les hauteurs produites, dans l'ordre croissant. */
const PRODUITES = [64, 128, 256]

/** Au moins le double de la hauteur demandée — voir `Marque`. */
function sourceLaPlusProche(taille: number): number {
  return PRODUITES.find((t) => t >= taille * 2) ?? 256
}
